import { useEffect, useState } from "react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const STATUS_OPTIONS_CREATE = [
  { value: "active", label: "Ativa (publicar já)" },
  { value: "pending", label: "Pendente (moderação)" },
];
const STATUS_OPTIONS_EDIT = [
  { value: "active", label: "Ativa" },
  { value: "pending", label: "Pendente" },
  { value: "closed", label: "Inativa / Fechada" },
  { value: "rejected", label: "Rejeitada" },
];

function emptyForm() {
  return {
    title: "", company_id: "", company_name: "", category: "", district: "",
    concelho: "", regime: "", work_mode: "", salary_min: "", salary_max: "",
    description: "", requirements: "", status: "active",
  };
}

export function AdminJobFormDialog({ open, onClose, meta, companies, job, onSaved }) {
  const isEdit = !!job;
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (job) {
      setForm({
        title: job.title || "", company_id: job.company_id || "",
        company_name: job.company_name || "", category: job.category || "",
        district: job.district || "", concelho: job.concelho || "",
        regime: job.regime || "", work_mode: job.work_mode || "",
        salary_min: job.salary_min ?? "", salary_max: job.salary_max ?? "",
        description: job.description || "", requirements: job.requirements || "",
        status: job.status || "active",
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, job]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDistrict = (v) => setForm((f) => ({ ...f, district: v, concelho: "" }));

  const concelhos = meta && form.district ? (meta.districts[form.district] || []) : [];

  const save = async () => {
    if (!form.title.trim()) return toast.error("Indica o título da vaga");
    if (!form.category || !form.district || !form.concelho || !form.regime || !form.work_mode)
      return toast.error("Preenche categoria, distrito, concelho, regime e modalidade");
    if (!form.description.trim()) return toast.error("Indica a descrição da vaga");
    if (!isEdit && !form.company_id && !form.company_name.trim())
      return toast.error("Escolhe uma empresa ou indica um nome de empresa");

    const payload = {
      title: form.title.trim(), category: form.category, district: form.district,
      concelho: form.concelho, regime: form.regime, work_mode: form.work_mode,
      salary_min: form.salary_min === "" ? null : parseInt(form.salary_min, 10),
      salary_max: form.salary_max === "" ? null : parseInt(form.salary_max, 10),
      description: form.description.trim(),
      requirements: form.requirements.trim() || null,
      status: form.status,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/admin/jobs/${job.id}`, payload);
        toast.success("Vaga atualizada");
      } else {
        if (form.company_id) payload.company_id = form.company_id;
        else payload.company_name = form.company_name.trim();
        await api.post("/admin/jobs", payload);
        toast.success("Vaga criada");
      }
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const statusOptions = isEdit ? STATUS_OPTIONS_EDIT : STATUS_OPTIONS_CREATE;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <DialogContent data-testid="admin-job-form-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tighter">
            {isEdit ? "Editar vaga" : "Nova vaga (interna)"}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? "Atualiza os detalhes e o estado desta vaga." : "Cria uma vaga diretamente no sistema, sem scraping."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Título *</Label>
            <Input data-testid="admin-job-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Ex.: Empregado de mesa" />
          </div>

          {isEdit ? (
            <div>
              <Label>Empresa</Label>
              <Input value={form.company_name} disabled className="bg-slate-50" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Empresa existente</Label>
                <Select value={form.company_id || "__none__"} onValueChange={(v) => set("company_id", v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="admin-job-company"><SelectValue placeholder="Escolher empresa" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="__none__">— Sem empresa (usar nome livre) —</SelectItem>
                    {(companies || []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ou nome de empresa</Label>
                <Input data-testid="admin-job-company-name" value={form.company_name} disabled={!!form.company_id}
                  onChange={(e) => set("company_name", e.target.value)} placeholder="Nome da empresa" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Categoria *</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="admin-job-category"><SelectValue placeholder="Escolher" /></SelectTrigger>
                <SelectContent>{(meta?.categories || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Distrito *</Label>
              <Select value={form.district} onValueChange={setDistrict}>
                <SelectTrigger data-testid="admin-job-district"><SelectValue placeholder="Escolher" /></SelectTrigger>
                <SelectContent className="max-h-64">{Object.keys(meta?.districts || {}).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Concelho *</Label>
              <Select value={form.concelho} onValueChange={(v) => set("concelho", v)} disabled={!form.district}>
                <SelectTrigger data-testid="admin-job-concelho"><SelectValue placeholder={form.district ? "Escolher" : "Escolhe distrito primeiro"} /></SelectTrigger>
                <SelectContent className="max-h-64">{concelhos.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Regime *</Label>
              <Select value={form.regime} onValueChange={(v) => set("regime", v)}>
                <SelectTrigger data-testid="admin-job-regime"><SelectValue placeholder="Escolher" /></SelectTrigger>
                <SelectContent>{(meta?.regimes || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Modalidade *</Label>
              <Select value={form.work_mode} onValueChange={(v) => set("work_mode", v)}>
                <SelectTrigger data-testid="admin-job-workmode"><SelectValue placeholder="Escolher" /></SelectTrigger>
                <SelectContent>{(meta?.work_modes || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Estado</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="admin-job-status"><SelectValue /></SelectTrigger>
                <SelectContent>{statusOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Salário mín. (€)</Label>
              <Input data-testid="admin-job-salary-min" type="number" value={form.salary_min} onChange={(e) => set("salary_min", e.target.value)} placeholder="Ex.: 900" />
            </div>
            <div>
              <Label>Salário máx. (€)</Label>
              <Input data-testid="admin-job-salary-max" type="number" value={form.salary_max} onChange={(e) => set("salary_max", e.target.value)} placeholder="Ex.: 1200" />
            </div>
          </div>

          <div>
            <Label>Descrição *</Label>
            <Textarea data-testid="admin-job-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={5} placeholder="Descreve a função, o dia-a-dia e o que a empresa oferece" />
          </div>
          <div>
            <Label>Requisitos</Label>
            <Textarea data-testid="admin-job-requirements" value={form.requirements} onChange={(e) => set("requirements", e.target.value)} rows={3} placeholder="Requisitos obrigatórios e valorizados (opcional)" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button data-testid="admin-job-save" className="rounded-full bg-slate-900 hover:bg-slate-800" onClick={save} disabled={saving}>
            {saving ? "A guardar…" : isEdit ? "Guardar alterações" : "Criar vaga"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AdminJobFormDialog;
